import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import {Component, OnInit} from '@angular/core';
import {ToasterService} from 'angular2-toaster';
import {TranslocoService} from '@jsverse/transloco';
import {DataService} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';

interface UserRow {
    rid: string;
    name: string;
    /** パスワードリセットの送信先 (issue #171)。未登録のユーザはリセットを受け取れない */
    email: string;
    roleRids: string[];
    roleNames: string;
}

interface RoleOption {
    rid: string;
    name: string;
}

/**
 * システム管理 > ユーザ管理。OUser に ORole (業務ライン別 7 ロール等) を割り当てる。
 * roles は LINK 集合のため partial update では設定できず、SQL command で
 * {@code UPDATE <rid> SET roles = [<rids>]} により明示更新する (member.component と同流儀)。
 */
@Component({
    standalone: false,
    selector: 'app-system-users',
    templateUrl: './system-users.component.html',
})
export class SystemUsersComponent implements OnInit {
    rows: UserRow[] = [];
    cv: wjcCore.CollectionView = new wjcCore.CollectionView([], {pageSize: 100});
    allRoles: RoleOption[] = [];
    loading = false;

    editingRid: string | null = null;
    /** 編集中ユーザのメールアドレス (issue #171) */
    email = '';
    checked: { [rid: string]: boolean } = {};

    constructor(private dataService: DataService, private toasterService: ToasterService, private transloco: TranslocoService) {
    }

    ngOnInit(): void {
        this.loadRoles();
        this.load();
    }

    /** 役割割当の一覧から外す OrientDB 組込みロール。 */
    private static readonly BUILTIN_ROLES = ['admin', 'writer', 'reader'];

    private loadRoles(): void {
        this.dataService.query({sql: 'SELECT FROM ORole ORDER BY name', count: 'SELECT FROM ORole'},
            ActivePromotionConfig.SERVER_URL).subscribe({
            next: (res: any) => {
                const roles = wjcCore.isArray(res.results) ? res.results : [];
                this.allRoles = roles
                    .filter((r: any) => SystemUsersComponent.BUILTIN_ROLES.indexOf(r.name) === -1)
                    .map((r: any) => ({rid: r['@rid'], name: r.name}));
            },
            error: () => this.toasterService.pop('error', this.transloco.translate('route.user_management'), this.transloco.translate('shell.roles_load_failed')),
        });
    }

    load(): void {
        this.loading = true;
        this.dataService.query({sql: 'SELECT FROM OUser ORDER BY name', count: 'SELECT FROM OUser', fetchPlan: '*:1'},
            ActivePromotionConfig.SERVER_URL).subscribe({
            next: (res: any) => {
                const users = wjcCore.isArray(res.results) ? res.results : [];
                this.rows = users.map((u: any) => {
                    const rids = this.roleRids(u.roles);
                    return {rid: u['@rid'], name: u.name, email: u.email || '', roleRids: rids, roleNames: this.roleNames(u.roles)};
                });
                this.cv = new wjcCore.CollectionView(this.rows, {pageSize: 100});
                this.loading = false;
            },
            error: () => {
                this.toasterService.pop('error', this.transloco.translate('route.user_management'), this.transloco.translate('shell.users_load_failed'));
                this.loading = false;
            },
        });
    }

    private roleRids(roles: any): string[] {
        if (!wjcCore.isArray(roles)) return [];
        return roles.map((r: any) => wjcCore.isObject(r) ? r['@rid'] : r).filter((x: any) => !!x);
    }

    private roleNames(roles: any): string {
        if (!wjcCore.isArray(roles)) return '';
        return roles.map((r: any) => wjcCore.isObject(r) ? r.name : r).filter((n: any) => n).join(', ');
    }

    edit(row: UserRow): void {
        this.editingRid = row.rid;
        this.email = row.email;
        this.checked = {};
        for (const rid of row.roleRids) {
            this.checked[rid] = true;
        }
    }

    cancel(): void {
        this.editingRid = null;
        this.email = '';
        this.checked = {};
    }

    /** 入力が空、または メールアドレスの形になっているか */
    get emailValid(): boolean {
        return !this.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim());
    }

    get editingName(): string {
        const row = this.rows.find((r) => r.rid === this.editingRid);
        return row ? row.name : '';
    }

    save(): void {
        if (!this.editingRid) return;
        if (!this.emailValid) {
            this.toasterService.pop('warning', this.transloco.translate('route.user_management'), this.transloco.translate('shell.email_invalid_format_short'));
            return;
        }
        const pickerRids = this.allRoles.map((r) => r.rid);
        const checkedRids = this.allRoles.filter((r) => this.checked[r.rid]).map((r) => r.rid);
        // 割当一覧に出さない組込みロール (admin/writer/reader) を既存ユーザが持つ場合は保持する。
        const row = this.rows.find((r) => r.rid === this.editingRid);
        const preserved = row ? row.roleRids.filter((rid) => pickerRids.indexOf(rid) === -1) : [];
        const rids = checkedRids.concat(preserved);
        const sql = 'UPDATE OUser SET roles = [' + rids.join(',') + '] WHERE @rid = ' + this.editingRid;
        this.dataService.command({sql: sql}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: () => this.saveEmail(),
            error: () => this.toasterService.pop('error', this.transloco.translate('route.user_management'), this.transloco.translate('shell.update_failed')),
        });
    }

    /**
     * メールアドレスを保存する (issue #171)。
     *
     * <p>値を SQL に埋め込まず、部分更新 ({@code /rest/data/update?mode=partial}) で
     * email だけを書き換える。他の項目 (パスワードや役割) には触らない。
     */
    private saveEmail(): void {
        const doc: any = {'@class': 'OUser', '@rid': this.editingRid, email: this.email.trim()};
        this.dataService.update([doc], ActivePromotionConfig.SERVER_URL).subscribe({
            next: () => {
                this.toasterService.pop('info', this.transloco.translate('route.user_management'), this.transloco.translate('shell.updated'));
                this.cancel();
                this.load();
            },
            error: () => {
                // 役割は更新済み。メールだけ失敗したことが分かるようにする
                this.toasterService.pop('error', this.transloco.translate('route.user_management'), this.transloco.translate('shell.email_update_failed'));
                this.load();
            },
        });
    }
}
